import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import MaintenanceForm from '../../components/maintenance/MaintenanceForm';
import { MaintenanceRecord } from '../../types/maintenance';
import { getMaintenanceRecord, updateMaintenanceRecord } from '../../services/maintenanceService';
import { Vehicle } from '../../types/vehicle';
import vehicleService from '../../services/vehicleService';
import { getSuppliers } from '../../services/supplierService';
import { Timestamp } from 'firebase/firestore';

const EditMaintenanceRecordPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser, currentOrganization } = useAuth();
  const [maintenance, setMaintenance] = useState<MaintenanceRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<Array<{ id: string; name: string }>>([]);
  const [serviceProviders, setServiceProviders] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    const loadData = async () => {
      if (!id || !currentOrganization) return;

      try {
        const [maintenanceData, vehicleList, supplierList] = await Promise.all([
          getMaintenanceRecord(id),
          vehicleService.getVehicles(currentOrganization.id),
          getSuppliers(currentOrganization.id)
        ]);

        if (!maintenanceData) {
          setError(t('maintenance.recordNotFound'));
          return;
        }

        setMaintenance(maintenanceData);
        setVehicles(vehicleList.map(vehicle => ({
          id: vehicle.id,
          name: `${vehicle.make} ${vehicle.model} - ${vehicle.licensePlate}`
        })));
        setServiceProviders(supplierList
          .map(supplier => ({
            id: supplier.id || '',
            name: supplier.name
          }))
          .filter(s => s.id)
        );
      } catch (error) {
        console.error('Error loading data:', error);
        setError(t('maintenance.errors.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, currentOrganization, t]);

  const handleSubmit = async (data: Partial<MaintenanceRecord>) => {
    if (!id || !currentUser) return;

    try {
      await updateMaintenanceRecord(id, {
        ...data,
        updatedBy: currentUser.id,
        updatedAt: Timestamp.now(),
      });
      navigate(`/admin/maintenance/${id}`);
    } catch (error) {
      console.error('Error updating maintenance record:', error);
      setError(t('maintenance.errors.saveFailed'));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !maintenance) {
    return (
      <Box p={3}>
        <Typography color="error">{error || t('maintenance.recordNotFound')}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('maintenance.form.editTitle')}
        </Typography>
        <MaintenanceForm
          maintenance={maintenance}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/admin/maintenance/${id}`)}
          vehicles={vehicles}
          serviceProviders={serviceProviders}
        />
      </Box>
    </Container>
  );
};

export default EditMaintenanceRecordPage;
